import axios from "axios";
import {useState, useEffect, useRef} from "react";
import {Link, redirect, useLoaderData} from "react-router-dom";
import {Header} from "../layout/header.js";
import {Footer} from "../layout/footer.js";
import {GenericBanner} from "../components/genericbanner.js";
import {MissionListTable} from "../components/missionlisttable.js";
import learnPageBanner from "../images/learnpage-bg.jpg";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';


/**
 * React-router-dom Loader function to load backend endpoint with
 * AJAX, the response from which drives the page.
 */
export async function loader() {
    try {
        const response = await axios.get(
            `/heliodata/app/load_missionlist_page`
        );
        return {missionListData: response.data};
    }
    catch (error) {
        return redirect("/error");
    }
}


/**
 * React component to display the mission listing page.
 */
export function MissionListPageContent() {
    const {missionListData} = useLoaderData();
    const [filterText, setFilterText] = useState('');
    const [allGroupedMissions, setAllGroupedMissions] = useState(null);
    const [filteredVariables, setFilteredVariables] = useState(null);
    const [stats, setStats] = useState(null);
    const loadingIconRef = useRef(null);


    // Recursive sort helper
    const sortHierarchy = (items) => {
        items.sort((a, b) => a.hcutId.localeCompare(b.hcutId));
        items.forEach(item => {
            if (item.children && item.children.length > 0) {
                sortHierarchy(item.children);
            }
        });
    };

    const groupByUnderscorePrefix = (items) => {
        if (!Array.isArray(items)) return [];

        const groupedMap = {};
        const others = [];

        items.forEach(item => {
            const match = item.hcutId.match(/^([A-Za-z0-9]+)_/);

            if (match) {
                const parent = match[1];
                if (!groupedMap[parent]) groupedMap[parent] = [];
                groupedMap[parent].push(item);
            } else {
                others.push(item);
            }
        });

        Object.entries(groupedMap).forEach(([parent, children]) => {
            const existingParent = others.find(item => item.hcutId === parent);
            if (existingParent) {
                // Merge children safely (avoid duplicates)
                children.forEach(child => {
                    if (!existingParent.children.some(c => c.hcutId === child.hcutId)) {
                        existingParent.children.push(child);
                    }
                });
            } else {
                others.push({
                    hcutId: parent,
                    name: parent,
                    noLink: true,
                    children
                });
            }
        });

        sortHierarchy(others);
        return others;
    };

    const groupByNumberSuffix = (items) => {
        if (!Array.isArray(items)) return [];

        const groupedMap = {};
        const others = [];

        items.forEach(item => {
            const match = item.hcutId.trim().match(/^([A-Za-z-]+?)_?(\d+)$/);
            if (match) {
                let base = match[1].replace("-", "");
                if (!groupedMap[base]) groupedMap[base] = [];
                groupedMap[base].push(item);
            } else {
                others.push(item);
            }
        });

        Object.entries(groupedMap).forEach(([base, children]) => {
            const existingParent = others.find(item => item.hcutId === base);
            if (existingParent) {
                children.forEach(child => {
                    if (!existingParent.children.some(c => c.hcutId === child.hcutId))
                        existingParent.children.push(child);
                });
            } else if (children.length > 1) {
                others.push({ hcutId: base, name: base, noLink: true, children });
            } else {
                others.push(children[0]);   // lone numbered mission (AeroCube-6) stays top-level
            }
        });

        sortHierarchy(others);
        return others;
    };


    const groupInterball = (items) => {
        if (!Array.isArray(items)) return [];

        const prefix = "Interball";
        const children = [];
        const others = [];

        items.forEach(item => {
            if (item.hcutId === prefix) {
                others.push({ ...item, children: item.children ? [...item.children] : [] });
                return;
            }

            if (item.hcutId.startsWith(prefix + "-")) {
                children.push(item);
            } else {
                others.push(item);
            }
        });

        if (children.length === 0) return others;

        const existingParent = others.find(i => i.hcutId === prefix);

        if (existingParent) {
            children.forEach(child => {
                if (!existingParent.children.some(c => c.hcutId === child.hcutId)) {
                    existingParent.children.push(child);
                }
            });
        } else {
            others.push({
                hcutId: prefix,
                name: prefix,
                children: children
            });
        }

        const parent = others.find(i => i.hcutId === prefix);
        parent.children.sort((a, b) => a.hcutId.localeCompare(b.hcutId));

        others.sort((a, b) => a.hcutId.localeCompare(b.hcutId));

        return others;
    };


    const buildHierarchy = (items) => {
        if (!Array.isArray(items)) return [];

        const map = {};
        const roots = [];

        items.forEach(item => {
            map[item.hcutId] = { ...item, children: [] };
        });

        items.forEach(item => {
            const lastDelim = Math.max(item.hcutId.lastIndexOf("_"), item.hcutId.lastIndexOf("-"));
            if (lastDelim !== -1) {
                const parentId = item.hcutId.substring(0, lastDelim);
                if (map[parentId]) {
                    map[parentId].children.push(map[item.hcutId]);
                } else {
                    roots.push(map[item.hcutId]);
            }
            } else {
                roots.push(map[item.hcutId]);
            }
        });

        let groundCleaned = groupGroundItems(roots);
        let nDSuffixCleaned = groupNumberDashSuffix(groundCleaned);
        let suffixCleaned = groupByNumberSuffix(nDSuffixCleaned);
        let underscoreCleaned = groupByUnderscorePrefix(suffixCleaned);
        let finalHierarchy = groupInterball(underscoreCleaned);

        sortHierarchy(finalHierarchy);
        setStats(countHierarchyStats(finalHierarchy));

        return finalHierarchy;
    };

    const groupGroundItems = (items) => {
        if (!Array.isArray(items)) return [];

        const isGroundLike = (id) =>
            id.startsWith("Ground_") || id.startsWith("THEMIS_Ground");

        let groundChildren = items.filter(item => isGroundLike(item.hcutId));


        // Remove duplicates where THEMIS overrides Ground_*
        const themisStations = groundChildren.filter(item =>
            item.hcutId.startsWith("THEMIS_Ground")
        );

        const groundStations = groundChildren.filter(item =>
            item.hcutId.startsWith("Ground_")
        );


        const dedupedGround = [...groundChildren];

        for (const themis of themisStations) {
            const themisShortName = themis.name;
            themis.name = themis.shortName;
            themis.shortName = themisShortName;
            let trueName = null;

            const match = themis.name.match(/NASA THEMIS GBO\s+(.*)\s+Station/i);
            if (match && match[1]) {
                trueName = match[1].trim();
            }

            if (!trueName) continue;


            for (const g of groundStations) {
                console.log(`The g is ${JSON.stringify(g)} ${themisShortName}`);
                if (g.shortName.startsWith(trueName) ||
                    g.name.startsWith(trueName) ||
                    g.name.endsWith(`Code: ${themisShortName}`) ||
                    (g.name.endsWith(`Code: WHS`) && themisShortName === "WHIT") || /*Unique case for white horse, fine for now till Spase is fixed*/
                    (g.hcutId.includes(`Ground_Whitehorse`) && themisShortName === "NASA THEMIS GBO White Horse Station")) {

                    const index = dedupedGround.indexOf(g);
                    if (index !== -1) {
                        dedupedGround.splice(index, 1);
                    }
                }
            }
        }

        let others = items.filter(item => !isGroundLike(item.hcutId));

        // Add Ground parent if there are children
        if (dedupedGround.length > 0) {
            others.push({
                hcutId: "GBO",
                name: "Ground Based Observatory",
                noLink: true,
                children: dedupedGround
            });
        }

        others.sort((a, b) => a.hcutId.localeCompare(b.hcutId));

        return others;
    };

    const groupNumberDashSuffix = (items, exclusions = []) => {
        if (!Array.isArray(items)) return [];

        const groupedMap = {};
        const others = [];

        items.forEach(item => {

            if (exclusions.includes(item.hcutId)) {
                return;
            }

            // Match pattern: prefix + number + '-' + letters
            const match = item.hcutId.match(/^([A-Za-z]+)\d+-[A-Za-z]+$/);
            if (match) {
                let base = match[1];
                base = base.replace("-", "");
                if (!groupedMap[base]) groupedMap[base] = [];
                groupedMap[base].push(item);
            } else {
                others.push(item);
            }
        });

        // Create new parent items for each group
        Object.entries(groupedMap).forEach(([base, children]) => {
            others.push({
                hcutId: base,
                name: base,
                noLink: true,
                children: children
            });
        });

        others.sort((a, b) => a.hcutId.localeCompare(b.hcutId));

        return others;
    };

    const countHierarchyStats = (items) => {
        let parents = 0;
        let children = 0;

        const traverse = (nodeList) => {
            nodeList.forEach(node => {
                if (node.children && node.children.length > 0) {
                    parents++;
                    traverse(node.children);
                } else {
                    children++;
                }
            });
        };

        traverse(items);

        return {
            parents,
            children,
            total: parents + children
        };
    };

    useEffect(() => {

        if (!missionListData) return;

        const groupedMissionList = buildHierarchy(missionListData.missions);

        let filtered = groupedMissionList;

        if (filterText.toLowerCase().trim() !== '') {
                const filterTerms = filterText
                        .split(',')
                        .map(term => term.trim().toLowerCase())
                        .filter(term => term.length > 0);

                const startsWithMatches = [];
                const containsMatches = [];

                groupedMissionList.forEach(parent => {

                        const parentStartsWith = filterTerms.some(term =>
                                parent.hcutId.toLowerCase().startsWith(term) ||
                                parent.shortName?.toLowerCase().startsWith(term) ||
                                parent.name.toLowerCase().startsWith(term)
                        );

                        const parentContains = filterTerms.some(term =>
                                parent.hcutId.toLowerCase().includes(term) ||
                                parent.name.toLowerCase().includes(term)
                        );

                        if (parentStartsWith) {
                                startsWithMatches.push(parent);
                                return;
                        }

                        if (parentContains) {
                                containsMatches.push(parent);
                                return;
                        }

                        const matchingChildren = parent.children?.filter(child => {

                                const childStartsWith = filterTerms.some(term =>
                                        child.hcutId.toLowerCase().startsWith(term) ||
                                        child.shortName?.toLowerCase().startsWith(term) ||
                                        child.name.toLowerCase().startsWith(term)
                                );

                                const childContains = filterTerms.some(term =>
                                        child.hcutId.toLowerCase().includes(term) ||
                                        child.name.toLowerCase().includes(term)
                                );

                                if (childStartsWith) {
                                        startsWithMatches.push(child);
                                        return false;
                                }

                                if (childContains) {
                                        containsMatches.push(child);
                                        return false;
                                }

                                return false;
                        }) || [];
                });

                filtered = [...startsWithMatches, ...containsMatches];
        }
        setFilteredVariables(filtered);
        setAllGroupedMissions(groupedMissionList);
    }, [filterText, missionListData]);


    return (
        <>
          <Header wide={false} />
          <GenericBanner
            title={"Mission and Observatory List"}
            tagline="Full list of available missions and observatories in the Heliodata system."
	    bannerImage={learnPageBanner}
       	    articleContent={false}
            breadcrumbLevels={[
                {link: `/missionlist/`, text: "Missions and Observatories"},
            ]}
          />

	    <div className="pageContainer pageContainerArticleContent">
                <p>Total of {stats?.total} items.</p>
              <div className="missionListFilterContainer">
                <input
                  type="text"
                  placeholder="Filter missions (ACE, MMS)"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                  className="cdaWebModalVariableInput"
                  spellCheck="false"
              />
              <FontAwesomeIcon icon={faSearch} className="cdaWebModalVariableInputIcon" />
              </div>
              <MissionListTable groupedData={filteredVariables} />
            </div>

          <Footer />
        </>
    );
}
